import type { World } from '@/core/ecs/World'
import type { SceneBridge } from '@/gfx/SceneBridge'
import type { PlayerInput } from '@/input/InputManager'
import { NetPlayback, type PlaybackStep } from './NetPlayback'
import type { NetClient } from './NetClient'
import { packInput } from './protocol'

/** Between two sends of a changing stick; presses always go at once. */
const MIN_SEND_MS = 12
/**
 * An unchanged stick is still sent this often: the server hands a player who
 * goes quiet for 500 ms to a bot (a dropped line the socket has not noticed).
 */
const KEEPALIVE_MS = 100
const PING_MS = 2000

/**
 * This browser in a room: its connection, and the match drawn from it.
 *
 * Once per rendered frame it sends this slot's input — at once when a button
 * went down, otherwise when the stick or a held button changed, and at least
 * every 100 ms — and plays the snapshots back into the local world.
 */
export class OnlinePlay {
  readonly playback: NetPlayback
  private lastSent = ''
  private lastSentAt = -Infinity
  private lastPing = -Infinity

  constructor(
    readonly client: NetClient,
    world: World,
    bridge: SceneBridge,
  ) {
    this.playback = new NetPlayback(world, bridge)
    client.on('snapshot', (snap) => this.playback.receive(snap, performance.now()))
  }

  /** This browser's fighter in the running match, or null: watching, or between matches. */
  get slot(): number | null {
    const room = this.client.room
    return room?.members.find((m) => m.id === room.you)?.slot ?? null
  }

  frame(now: number, input: PlayerInput): PlaybackStep {
    if (this.slot !== null) this.send(now, input)
    if (now - this.lastPing > PING_MS) {
      this.client.ping(now)
      this.lastPing = now
    }
    return this.playback.advance(now)
  }

  private send(now: number, input: PlayerInput): void {
    const { mx, mz, down, pressed } = packInput(input, 0)
    const state = `${mx},${mz},${down}`
    const since = now - this.lastSentAt
    const due = since >= KEEPALIVE_MS || (state !== this.lastSent && since >= MIN_SEND_MS)
    if (pressed === 0 && !due) return
    this.client.sendInput(input)
    this.lastSent = state
    this.lastSentAt = now
  }
}
